import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';

import { ARRETES } from '@/constants';
import { postArrete } from '@/routes';
import { getHttpStatus } from '@/utils/apiErrors';

interface UseCreateArreteAnnuelResult {
  createArreteAnnuel: (annee: number) => Promise<boolean>;
  creating: boolean;
}

/**
 * Hook de création d'un arrêté annuel.
 * Encapsule l'appel `POST /arretes { type: "Annuel" }`, le toast de succès et la gestion
 * silencieuse du 422 (règle métier refusée côté back). Renvoie `true` en cas de succès pour
 * laisser l'appelant enchaîner ses propres actions (rafraîchissement, navigation…).
 * `annee` sert uniquement au libellé du toast.
 */
const useCreateArreteAnnuel = (): UseCreateArreteAnnuelResult => {
  const [creating, setCreating] = React.useState(false);

  const createArreteAnnuel = React.useCallback(async (annee: number): Promise<boolean> => {
    setCreating(true);
    try {
      await request.post(postArrete(), { type: ARRETES.TYPE_ANNUEL });
      toast(`L'arrêté annuel ${annee} a bien été créé.`, { type: 'success' });
      return true;
    } catch (error: any) {
      // 422 = règle métier refusée côté back (hors période / déjà créé) → géré silencieusement
      if (getHttpStatus(error) !== 422) {
        toast.error("Une erreur est survenue lors de la création de l'arrêté annuel");
      }
      return false;
    } finally {
      setCreating(false);
    }
  }, []);

  return { createArreteAnnuel, creating };
};

export default useCreateArreteAnnuel;
